import { Platform, Product, isThemeFree } from '../../model/product/product.model';
import { platformDisplayName } from '../platform/getting-started.data';
import { THEME_PALETTES } from '../palette/palette.data';
import { PLUGINS, THEMES } from './product.data';

/** a palette's store platform -> its app's name ('aurora-borealis/figma-palette' is the Aurora Borealis palette for Figma) */
export const PALETTE_APPS: { [ platformKey: string ]: string } = {
  'figma-palette': 'Figma',
  'coreldraw-palette': 'CorelDRAW',
  'macos-palette': 'macOS',
};

/**
 * the store's apps (2026-10-14: the macOS Color Picker and Tidy Desktop, sold here as well as on the Mac App Store): sku -> the app's
 * product in PLUGINS and its platform. An app is not a theme: it keeps its own name ("Color Picker") and links to
 * its own page. The sku's first part must also be in the server's APP_PRODUCTS (orchestration-server store.util.ts).
 */
export const STORE_APPS: { [ sku: string ]: { productKey: string; platformName: string; platformLogo: string; listPrice: { amount: number; currency: string } } } = {
  'ai-token-meter/macos': {
    productKey: 'macos-ai-token-meter',
    platformName: 'macOS',
    platformLogo: './assets/third-parties/logo/macos-apps.webp',
    // owner 2026-10-19: $3.99 one-time; the Stripe Price is the source of truth
    listPrice: { amount: 399, currency: 'usd' },
  },
  'color-picker/macos': {
    productKey: 'macos-color-picker',
    platformName: 'macOS',
    platformLogo: './assets/third-parties/logo/macos-apps.webp',
    // the display fallback while the store API is unreachable; the Stripe Price is the source of truth
    listPrice: { amount: 299, currency: 'usd' },
  },
  'tidy-desktop/macos': {
    productKey: 'macos-tidy-desktop',
    platformName: 'macOS',
    platformLogo: './assets/third-parties/logo/macos-apps.webp',
    // the App Store's US price (owner 2026-10-14); the Stripe Price is the source of truth
    listPrice: { amount: 1499, currency: 'usd' },
  },
};

/** an app's list price (the display fallback), or undefined for any other sku */
export function storeAppListPrice( sku: string ): { amount: number; currency: string } | undefined {
  return STORE_APPS[ sku ]?.listPrice;
}

/** the store platform of a palette app (THEME_PALETTES platform key 'figma' -> 'figma-palette') */
export function paletteSku( themeKey: string, paletteKey: string ): string {
  return `${ themeKey }/${ paletteKey }-palette`;
}

/** A theme found by key, with the product line it belongs to. */
export interface ProductHit {
  product: Product;
  category: string;
}

/** What the store knows about a SKU on the site side: the theme, and the platform listing it names. */
export interface StoreListing {
  sku: string;
  productKey: string;
  platformKey: string;
  /** null when the site data has no theme with this key (renders from the store's own names) */
  product: Product | null;
  category: string;
  /** the listing (Platform) carrying this sku; null when the site data has no such listing (yet) */
  platform: Platform | null;
  /** display name - the listing's, else derived from the key ('sublime-text' -> 'Sublime Text') */
  platformName: string;
  platformLogo: string;
  /** the listing's screenshot, else the theme logo */
  thumb: string | null;
  /** a theme's color palette (2026-10-12: the free ones through the cart), not a theme port */
  palette?: boolean;
  /** an app (2026-10-14), not a theme: `product` is its PLUGINS entry, `appUrl` its page */
  app?: boolean;
  appUrl?: string;
}

/** 'ocean' -> the Ocean theme (and its line), or null. */
export function findProductByKey( key: string | undefined | null ): ProductHit | null {
  if ( !key ) return null;
  for ( const line of THEMES ) {
    const product = line.products.find( p => p.key === key );
    if ( product ) return { product, category: line.name };
  }
  return null;
}

/**
 * 'ocean/sublime-text' -> the theme + the platform listing that carries the
 * sku. The cart and the order page both render lines from this so a sku
 * always resolves the same way; a sku the site data no longer knows still
 * resolves (product null) and renders from the store's own names.
 */
export function findStoreListing( sku: string ): StoreListing | null {
  const slash = ( sku || '' ).indexOf( '/' );
  if ( slash <= 0 ) return null;
  const productKey = sku.slice( 0, slash );
  const platformKey = sku.slice( slash + 1 );
  const storeApp = STORE_APPS[ sku ];
  if ( storeApp ) {
    const line = PLUGINS.find( l => l.products.some( p => p.key === storeApp.productKey ) );
    const appProduct = line?.products.find( p => p.key === storeApp.productKey ) ?? null;
    return {
      sku,
      productKey,
      platformKey,
      product: appProduct,
      category: line?.name ?? '',
      platform: null,
      platformName: storeApp.platformName,
      platformLogo: storeApp.platformLogo,
      thumb: appProduct?.logo || null,
      app: true,
      appUrl: appProduct?.url || '',
    };
  }
  const hit = findProductByKey( productKey );
  if ( PALETTE_APPS[ platformKey ] ) {
    const palettePlatform = THEME_PALETTES.find( t => t.key === productKey )?.platforms
      .find( pl => `${ pl.key }-palette` === platformKey );
    return {
      sku,
      productKey,
      platformKey,
      product: hit?.product ?? null,
      category: hit?.category ?? '',
      platform: null,
      platformName: PALETTE_APPS[ platformKey ],
      platformLogo: palettePlatform?.logo || '',
      thumb: palettePlatform?.screenshot?.src || hit?.product.logo || null,
      palette: true,
    };
  }
  const fallbackName = platformDisplayName( platformKey );
  const platform = hit?.product.platforms?.find( p => p.sku === sku )
    ?? hit?.product.platforms?.find( p => ( p.info?.name || '' ).toLowerCase() === fallbackName.toLowerCase() )
    ?? null;

  return {
    sku,
    productKey,
    platformKey,
    product: hit?.product ?? null,
    category: hit?.category ?? '',
    platform,
    platformName: platform?.info?.name || fallbackName,
    platformLogo: platform?.info?.logo || '',
    thumb: platform?.screenshot?.src || hit?.product.logo || null,
  };
}

/**
 * a free listing of the store (2026-09-21: free downloadable platforms go through the cart too):
 * the platform entry carries a sku and is not paid. Free lines are one copy and cost nothing;
 * a cart of free listings only becomes a free order (no Stripe).
 */
export function isFreeSku( sku: string ): boolean {
  const listing = findStoreListing( sku );
  // a free theme's palette is free
  if ( listing?.palette ) {
    return !!listing.product && isThemeFree( listing.product );
  }
  return !!listing?.platform?.sku && listing.platform.paid !== true;
}

/** the line's name as the store says it: "Ocean Theme", or "Ocean Palette" for a palette; an app keeps its own ("Color Picker") */
export function storeItemName( listing: StoreListing | null, fallback: string ): string {
  if ( listing?.app ) return ( listing.product?.name || fallback || '' ).trim();
  const base = ( listing?.product?.name || fallback || '' ).trim().replace( /\s+(theme|palette)$/i, '' );
  return `${ base } ${ listing?.palette ? 'Palette' : 'Theme' }`;
}

/** every theme with at least one listing sold on the site (for "you might like" strips). */
export function storeProducts(): ProductHit[] {
  const out: ProductHit[] = [];
  for ( const line of THEMES ) {
    for ( const product of line.products ) {
      if ( product.platforms?.some( p => !!p.sku ) ) out.push( { product, category: line.name } );
    }
  }
  return out;
}
